import * as Constants from "@/shared/constants";
import type {Weekday} from "@/shared/constants";
import type {BreakModel, FieldError} from "@/shared/timr";
import {FixedBreakType} from "@/shared/timrTypes";
import {
	EMPTY_FIXED_BREAK,
	type FixedBreakKey
} from "@/features/admin/breakModel/fixedBreakConfigurationModel";

interface ParsedFixedBreakField {
	fixedBreakKey: FixedBreakKey;
	weekday: Weekday | null;
	breakNumber: string;
}

/**
 * Clears the fixed-break fields that do not belong to the model's selected `fixedBreakType`.
 *
 * The UI deliberately keeps every fixed-break field populated at once so the user does not lose
 * input when toggling the type back and forth. Before persisting we must keep only the active
 * type's data, otherwise the inactive side would be saved as orphaned configuration.
 *
 * Mutates the passed break model.
 */
export function clearInactiveFixedBreakFields(breakModel: BreakModel): void {
	const clearFixedBreak = (fixedBreakKey: FixedBreakKey) => {
		breakModel[fixedBreakKey] = {...EMPTY_FIXED_BREAK};
	};

	switch (breakModel.fixedBreakType) {
		case FixedBreakType.NONE:
			clearFixedBreak("fixedBreak");
			Constants.WEEKDAYS.forEach(day => clearFixedBreak(`fixedBreak${day}`));
			break;
		case FixedBreakType.ALL_DAYS_EQUAL:
			Constants.WEEKDAYS.forEach(day => clearFixedBreak(`fixedBreak${day}`));
			break;
		case FixedBreakType.PER_DAY:
			clearFixedBreak("fixedBreak");
			break;
	}
}

/**
 * Parses a backend field error name into its fixed-break parts.
 *
 * The backend (BreakModelValidator) emits names in the shape `<key>.<from|to><1-3>`,
 * where the key is either the shared `fixedBreak` or a per-day `fixedBreak<Weekday>`.
 * For example, when the user configures a per-day fixed break from 13:00 to 12:30
 * on Monday, the backend will report `fixedBreakMonday.from1`. The dialog then needs
 * to treat that technical field name as "Monday, fixed break 1" so it can mark the
 * complete from/to range and render a compact validation summary in the React panel.
 *
 * Example:
 * - `fixedBreakMonday.from1` -> `{ fixedBreakKey: "fixedBreakMonday", weekday: "Monday", breakNumber: "1" }`
 * - `fixedBreak.to2` -> `{ fixedBreakKey: "fixedBreak", weekday: null, breakNumber: "2" }`
 *
 * Returns null for any field that is not a fixed-break field.
 */
function parseFixedBreakField(field: string): ParsedFixedBreakField | null {
	// Fixed-break validation fields use a nested property path, e.g. "fixedBreakMonday.from1".
	// Unrelated field errors such as "paidBreakDuration" do not have this shape.
	const separatorIndex = field.indexOf(".");
	if (separatorIndex === -1) {
		return null;
	}

	const fixedBreakKey = field.slice(0, separatorIndex);
	// Matches the range suffix the backend appends to a fixed-break key, e.g. "from1" / "to3".
	const rangeMatch = field.slice(separatorIndex + 1).match(/^(from|to)([123])$/);
	if (!rangeMatch || !fixedBreakKey.startsWith("fixedBreak")) {
		return null;
	}

	// Everything after "fixedBreak" is either a weekday ("Monday") or empty for
	// the shared all-days-equal configuration ("fixedBreak.from1").
	const weekday = fixedBreakKey.slice("fixedBreak".length);
	if (weekday && !Constants.WEEKDAYS.includes(weekday as Weekday)) {
		return null;
	}

	return {
		fixedBreakKey: fixedBreakKey as FixedBreakKey,
		weekday: weekday ? (weekday as Weekday) : null,
		breakNumber: rangeMatch[2]
	};
}

export function isFixedBreakFieldError(fieldError: FieldError): boolean {
	return parseFixedBreakField(fieldError.field) !== null;
}

/**
 * Returns both range fields (from and to) belonging to a single fixed-break validation field.
 *
 * The backend reports a validation error on only one side of a time range (e.g. `fixedBreakMonday.from1`),
 * but for a better UI/UX the dialog needs to mark both the `from` and `to` inputs of that range.
 * For a fixed-break field this expands to `["<key>.from<n>", "<key>.to<n>"]`.
 * Any non-fixed-break field is returned unchanged as a single-element array.
 *
 * Example: `fixedBreakMonday.from1` -> `["fixedBreakMonday.from1", "fixedBreakMonday.to1"]`
 *
 * @param field the field name reported by the backend
 * @returns the from/to field pair for fixed-break fields, or `[field]` otherwise
 */
export function getFixedBreakRangeFields(field: string): string[] {
	const parsed = parseFixedBreakField(field);
	if (!parsed) {
		return [field];
	}
	return [`${parsed.fixedBreakKey}.from${parsed.breakNumber}`, `${parsed.fixedBreakKey}.to${parsed.breakNumber}`];
}

/**
 * Builds a readable summary line for a fixed-break validation error, used by the React fixed break error summary.
 *
 * If the error's field is a fixed-break field, the message is prefixed with a label identifying
 * the affected slot, e.g. "Monday, Fixed break 1: ..." for a per-day break or
 * "Fixed break 2: ..." for a shared break
 * Errors on non-fixed-break fields are returned unchanged.
 *
 * @param fieldError the validation error reported by the backend
 * @returns the prefixed summary message, or the raw message for non-fixed-break fields
 */
export function formatFixedBreakErrorSummaryMessage(fieldError: FieldError): string {
	const parsed = parseFixedBreakField(fieldError.field);
	if (!parsed) {
		return fieldError.message;
	}

	const labelParts: string[] = [];
	if (parsed.weekday) {
		labelParts.push(messages[`admin.clientSettings.weekday.${parsed.weekday.toUpperCase()}`] || parsed.weekday);
	}
	labelParts.push(`${messages["breakModel.fixedBreaks"]} ${parsed.breakNumber}`);

	return `${labelParts.join(", ")}: ${fieldError.message}`;
}

/** Rendered by the React cut-handling panel, so its errors are routed away from `FormUtil` too. */
export function isCutHandlingFieldError(fieldError: FieldError): boolean {
	return fieldError.field === "cutWarningThresholdDuration";
}
